import { Alert, FormGroup, FormSection, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IService } from '@common/interfaces/';
import { ManagerAuthorization, ServiceCode } from '@common/enums/';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization, getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils/';
import type { IGroupSetting } from '../../../../interfaces/groupSetting';
import type { IReactSelectOptions } from '../../../../interfaces/reactSelectOptions';

export interface IServiceGroupSettingProps {
    error?: string;
    onChange: (serviceGroupSettingId: string | null) => void;
    onDoValidate?: () => void;
    service: IService;
    serviceGroupSettingId: string | null;
}

export function ServiceGroupSetting(props: IServiceGroupSettingProps) {
    assertHasAuthorization([ManagerAuthorization.organizationAddEdit, ManagerAuthorization.employmentSettingsEdit]);

    const abortableFetch = useAbortableFetch();
    const [serviceGroupSettings, setServiceGroupSettings] = useState<IGroupSetting[]>([]);

    useEffect(() => {
        loadGroupSettings();
    }, []);

    if (serviceGroupSettings.length === 0 && props.service.code !== ServiceCode.GGZOembl) {
        return null;
    }

    return (
        <FormSection title={translateKey('common.careGroup')}>
            <FormGroup
                hasErrors={Boolean(props.error)}
                label={translateKey('common.careGroup')}
            >
                <Select
                    error={getValidationErrorTranslation(props.error)}
                    onChange={async (value) => props.onChange(value?.value ? value?.value : null)}
                    options={loadServiceGroupSettingOptions()}
                    placeholder={translateKey('serviceOrganizationSetting.selectCareGroup')}
                    value={
                        serviceGroupSettings.length > 0
                            ? {
                                  value: props.serviceGroupSettingId,
                                  label:
                                      serviceGroupSettings.length > 0
                                          ? serviceGroupSettings.filter(
                                                (item) => item.id === props.serviceGroupSettingId
                                            )[0]?.group.name
                                          : null,
                              }
                            : null
                    }
                />
            </FormGroup>
            {!hasRequiredServiceGroupSetting() && (
                <Alert appearance="warning">{translateKey('embloomQuestionnaires.noGroup')}</Alert>
            )}
            {props.service.code === ServiceCode.GGZOembl && hasEmbloomGroupCertificate() === false && (
                <Alert appearance="warning">{translateKey('embloomQuestionnaires.noCertificate')}</Alert>
            )}
        </FormSection>
    );

    function hasRequiredServiceGroupSetting() {
        if (props.service.code === ServiceCode.GGZOembl) {
            return Boolean(props.serviceGroupSettingId);
        }

        return true;
    }

    function hasEmbloomGroupCertificate() {
        if (props.serviceGroupSettingId) {
            const serviceGroupSetting =
                props.serviceGroupSettingId &&
                serviceGroupSettings.find((groupSetting) => groupSetting.id === props.serviceGroupSettingId);

            return Boolean(serviceGroupSetting && serviceGroupSetting.embloomGroupCertificate);
        }

        return 'NoServiceGroupSetting';
    }

    function loadServiceGroupSettingOptions() {
        return serviceGroupSettings
            .sort((a, b) => a.group.name.localeCompare(b.group.name))
            .map((groupSetting) => {
                return {
                    value: groupSetting.id,
                    label:
                        props.service.code === ServiceCode.GGZOembl && !groupSetting.embloomGroupCertificate
                            ? `${groupSetting.group.name} (${translateKey('embloomQuestionnaires.noCertificateShort')})`
                            : groupSetting.group.name,
                    isDisabled: props.service.code === ServiceCode.GGZOembl && !groupSetting.embloomGroupCertificate,
                };
            }) as IReactSelectOptions[];
    }

    async function loadGroupSettings() {
        const url = `/api/serviceGroupSettings?serviceId=${props.service.id}&isActive=true`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setServiceGroupSettings(result.json.items);
    }
}
